'use client';

import { useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { FileImage } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { FOOTER_REVEAL_TRANSFORM } from '@/shared/lib/bottom-bar-reveal';
import { NestedSheetStackProvider, PromoBenefits } from '@/shared/ui';
import { useAlbumIntro } from '../hooks/use-album-intro';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import { useProject } from '@/features/project/hooks/use-projects';
import type { AlbumLanguage } from '@/features/design/album';
import type { AlbumCostPredictionParams } from '../api/album-service';
import { useAlbumCostPrediction } from '../hooks/use-album-cost-prediction';
import { useAlbumPanel, type AlbumPanelAngle } from '../hooks/useAlbumPanel';
import { useHasCompletedMainRender } from '@/features/design/concept';
import { AlbumPanelSettings } from './AlbumPanelSettings';
import { AlbumsList } from './AlbumsList';
import { AlbumDuplicateDialog } from './AlbumDuplicateDialog';
import { AlbumDesignLineBanner } from './AlbumDesignLineBanner';
import { useAlbumDuplicatePrompt } from '../hooks/use-album-duplicate-prompt';
import { useAssistantAction } from '@/shared/assistant';

interface AlbumCollapsiblePanelProps {
  conceptId: string;
  conceptAngles: AlbumPanelAngle[];
  isArchived?: boolean;
  projectId?: string;
  conceptTitle?: string | null;
  conceptNumber?: number;
  coverImageUrl?: string | null;
  /** Tailwind `bottom-*` class for the floating Generate capsule — clears the
   *  bottom-nav island (single-column / stacked album tab). */
  footerBottomClassName?: string;
}

export function AlbumCollapsiblePanel({
  conceptId,
  conceptAngles,
  isArchived = false,
  projectId,
  conceptTitle,
  conceptNumber,
  coverImageUrl,
  footerBottomClassName = 'bottom-6',
}: AlbumCollapsiblePanelProps) {
  const t = useTranslations('album');
  const tCommon = useTranslations('common');
  // Server-driven benefits copy for the empty state — advertises what a
  // project album includes (pack-gated feature), shown above the settings when
  // no album exists yet. Two-column parity: AlbumListWithViewer.
  const { data: albumIntro } = useAlbumIntro(conceptId);
  const panelRef = useRef<HTMLDivElement>(null);

  // After the user confirms generation, scroll the panel into view so the new
  // in-progress card (which appears at the top of the list) is not left
  // off-screen above the generate button on single-column phone layouts.
  const scrollPanelIntoView = () => {
    requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const {
    // State
    highlightedAlbumId,
    selectedLanguage,
    setSelectedLanguage,

    // Album settings
    selectedTemplate,
    storedSectionToggles,
    storedSelectedLanguage,
    storedRequirements,
    storedMeasurementSystem,
    updateSetting,

    // Album operations
    albums,
    mergedAlbums,
    generateAlbum,
    lastGenerateResult,
    resetGenerateResult,
    deleteAlbum,
    downloadAlbum,
    isGenerating,
    isDeleting,
    hasAlbumInQueue,

    // Selected renders
    selectedRenders,
    selectedRenderUrls,

    // Cost-estimate picker (FE4)
    estimatePicker,
  } = useAlbumPanel({
    conceptId,
    conceptAngles,
    projectId,
  });

  const renderedCount = Object.keys(selectedRenders).length;
  const totalCount = conceptAngles.length;
  const hasPartialRenders = renderedCount > 0 && renderedCount < totalCount;

  // Server-predicted token cost for the CURRENT settings — shown as a suffix
  // on the Generate button. The cost-relevant params mirror the generate
  // request below 1:1 (memoized so the hook's debounce is stable).
  const costPredictionParams = useMemo<AlbumCostPredictionParams>(
    () => ({
      renders_count: renderedCount,
      include_materials: storedSectionToggles.includeMaterials,
      include_blueprints: storedSectionToggles.includeBlueprints,
      include_technical_details: storedSectionToggles.includeTechnicalDetails,
      include_preparation_guide: storedSectionToggles.includePreparationGuide,
      include_safety_requirements:
        storedSectionToggles.includeSafetyRequirements,
      include_building_regulations:
        storedSectionToggles.includeBuildingRegulations,
      // True only for the "New estimate" picker option — exactly when the
      // generate request spreads `generate_new_estimate: true` (see
      // useAlbumEstimatePicker.estimatePayload).
      generate_new_estimate:
        'generate_new_estimate' in estimatePicker.estimatePayload,
      selected_renders: Object.values(selectedRenders),
      language: selectedLanguage || storedSelectedLanguage,
      ...(storedMeasurementSystem !== 'auto' ? { measurement_system: storedMeasurementSystem } : {}),
      ...(storedRequirements ? { requirements: storedRequirements } : {}),
    }),
    [
      selectedRenders,
      selectedLanguage,
      storedSelectedLanguage,
      storedMeasurementSystem,
      storedRequirements,
      renderedCount,
      storedSectionToggles.includeMaterials,
      storedSectionToggles.includeBlueprints,
      storedSectionToggles.includeTechnicalDetails,
      storedSectionToggles.includePreparationGuide,
      storedSectionToggles.includeSafetyRequirements,
      storedSectionToggles.includeBuildingRegulations,
      estimatePicker.estimatePayload,
    ]
  );
  const {
    tokens: predictedTokens,
    isSignificantCost,
    warningMessage: costWarningMessage,
  } = useAlbumCostPrediction(conceptId, costPredictionParams);

  // Token-cost chrome (≈N suffix + walnut, token-worded cost warning) is
  // per-building: a token-mode building deducts from the visible wallet, an
  // unlimited building (Facade Kit / Pro Plan, server `pack.is_unlimited`)
  // hides token numerics. Mirrors the render effort token hint.
  const { data: projectResponse } = useProject(projectId ?? '', {
    enabled: !!projectId,
  });
  const showsTokenCost =
    projectResponse?.data?.attributes?.pack?.is_unlimited === false;

  // Funnel-priority gate: until the concept's first design exists, Generate is
  // disabled with a "generate a design first" hint — a brand-new user must
  // never meet a paywall before the free design step (Apple/Android parity).
  const hasCompletedMainRender = useHasCompletedMainRender(conceptId);

  const handleGenerateAlbum = (
    forceRegenerate = false,
    designLineId?: string
  ) => {
    if (!hasCompletedMainRender) return;
    const selectedRenderIds = Object.values(selectedRenders);

    // Once a design exists, entitlement stays server-driven: no client-side
    // plan/balance preflight. The server enforces limits and returns a 422
    // with an inline paywall payload; `use-album.ts` mutation onError routes
    // that to the shared `LimitPaywallDialog` via `useLimitErrorPaywall`.
    generateAlbum({
      // Template is the design axis. Send the actually selected template —
      // Pro holders can pick architect/studio; the server falls back to
      // standard for any not-yet-built design. Blueprints are a separate free
      // toggle that replaces the old full/short split.
      template: selectedTemplate,
      // Content-section flags — always sent (all default ON) so the server
      // records the snapshot explicitly. `include_blueprints` gates ONLY the
      // facade projections (blueprint drawings).
      include_blueprints: storedSectionToggles.includeBlueprints,
      include_technical_details: storedSectionToggles.includeTechnicalDetails,
      include_materials: storedSectionToggles.includeMaterials,
      include_preparation_guide: storedSectionToggles.includePreparationGuide,
      include_safety_requirements:
        storedSectionToggles.includeSafetyRequirements,
      include_building_regulations:
        storedSectionToggles.includeBuildingRegulations,
      language: selectedLanguage || storedSelectedLanguage,
      // Measurement system: sent only when the user explicitly picked Metric
      // or Imperial — "Auto" omits the field so the server decides.
      ...(storedMeasurementSystem !== 'auto'
        ? { measurement_system: storedMeasurementSystem }
        : {}),
      selected_renders: selectedRenderIds,
      // Estimate choice: explicit estimate_id (may be a sibling design's) or
      // generate_new_estimate — see useAlbumEstimatePicker.estimatePayload.
      ...estimatePicker.estimatePayload,
      ...(storedRequirements ? { requirements: storedRequirements } : {}),
      ...(forceRegenerate ? { force_regenerate: true } : {}),
      // "Update blueprint for this design": the line's next version.
      ...(designLineId ? { design_line_id: designLineId } : {}),
    });
  };

  // Duplicate-album prompt — shared with the two-column inspector.
  const duplicatePrompt = useAlbumDuplicatePrompt(
    lastGenerateResult,
    resetGenerateResult
  );

  const handleDownloadAlbum = (albumId?: string) => {
    downloadAlbum(albumId);
  };

  const isAlbumBusy = isGenerating || hasAlbumInQueue;
  const handleGenerateClick = () => {
    if (isAlbumBusy) return;
    handleGenerateAlbum();
    scrollPanelIntoView();
  };
  // The AI architect's "Create album" card presses this same button. Published
  // only once it can act: a card opens this tab and presses on mount, before
  // the design's renders have loaded, and the gate would swallow the press.
  useAssistantAction(
    'order_album',
    hasCompletedMainRender ? conceptId : null,
    handleGenerateClick
  );

  return (
    // pb-24 reserves space for the floating Generate capsule below (fixed,
    // bottom-6) so it never overlaps the last settings row (e.g. "Units") when
    // the list is short — mirrors the design tab's ScrollableRenderSettings.
    <div ref={panelRef} className="pb-24">
      {/* Empty-state promo ad — shown above the settings when no album exists
          yet (stacked layout: promo on top, settings below). Hidden once an
          album is generating or present. */}
      {mergedAlbums.length === 0 && !hasAlbumInQueue && albumIntro && (
        <div className="pb-4">
          <PromoBenefits
            icon={<FileImage />}
            title={albumIntro.title}
            subtitle={albumIntro.subtitle}
            bullets={albumIntro.bullets}
          />
        </div>
      )}

      {/* The line's blueprint was built for another design — offer an update. */}
      <div className="pb-4 empty:hidden">
        <AlbumDesignLineBanner
          projectId={projectId ?? ''}
          conceptId={conceptId}
          disabled={isArchived || Boolean(isGenerating)}
          onUpdate={(lineId) => handleGenerateAlbum(false, lineId)}
        />
      </div>

      {/* Albums list */}
      <AlbumsList
        albums={mergedAlbums}
        highlightedAlbumId={highlightedAlbumId}
        isDeleting={isDeleting}
        onDelete={deleteAlbum}
        onDownload={handleDownloadAlbum}
        conceptTitle={conceptTitle}
        conceptNumber={conceptNumber}
        coverImageUrl={coverImageUrl}
      />

      {/* Inline album settings (no drawer) — template / language / blueprints
          / requirements / extended thinking. The Generate confirmation floats
          as a capsule above the bottom nav (below), mirroring the design tab.
          `isStacked`: this panel renders only on the single-column concept page
          (the two-column layout uses AlbumListWithViewer), so its sub-panels
          must use the stacked fixed-overlay path — same as the design tab — and
          drive the bottom-nav hide while open (NestedSheetStackProvider). */}
      <NestedSheetStackProvider isStacked>
        <AlbumPanelSettings
          requirements={storedRequirements}
          onRequirementsChange={(value) => updateSetting('requirements', value)}
          isArchived={isArchived}
          selectedTemplate={selectedTemplate}
          onTemplateChange={(template) =>
            updateSetting('selectedTemplate', template)
          }
          sectionToggles={storedSectionToggles}
          onSectionToggleChange={(key, checked) =>
            updateSetting(key, checked)
          }
          selectedLanguage={selectedLanguage || storedSelectedLanguage}
          onLanguageChange={(lang) => {
            setSelectedLanguage(lang);
            updateSetting('selectedLanguage', lang as AlbumLanguage);
          }}
          measurementSystem={storedMeasurementSystem}
          onMeasurementSystemChange={(system) =>
            updateSetting('measurementSystem', system)
          }
          hasPartialRenders={hasPartialRenders}
          renderedCount={renderedCount}
          totalCount={totalCount}
          selectedRenderUrls={selectedRenderUrls}
          estimatePicker={estimatePicker}
          isGenerating={isGenerating ?? false}
          // Server-localized significant-cost warning — null while an album is
          // queued so the banner never shows alongside the generating state.
          // Token-worded — legacy users only (P4.3).
          costWarningMessage={
            hasAlbumInQueue || !showsTokenCost ? null : costWarningMessage
          }
        />
      </NestedSheetStackProvider>

      {/* Duplicate album confirmation dialog (shared component) */}
      <AlbumDuplicateDialog
        open={duplicatePrompt.open}
        onOpenChange={duplicatePrompt.setOpen}
        generatedAt={duplicatePrompt.generatedAt}
        onUseExisting={duplicatePrompt.dismiss}
        onGenerateNew={() => {
          duplicatePrompt.dismiss();
          handleGenerateAlbum(true);
        }}
      />

      {/* Floating Generate capsule — above the bottom nav island, shaped like
          it, always visible over the scroll (Apple/Android parity with the
          design tab). Generation gating is server-driven (422 → paywall). */}
      {!isArchived && (
        <div
          className={cn(
            'fixed inset-x-0 z-40 flex justify-center px-4 pointer-events-none',
            footerBottomClassName
          )}
          // Ride the scroll-linked nav reveal (mirrors the Design tab footer).
          style={{
            transform: FOOTER_REVEAL_TRANSFORM,
            // Center over the content column, not the viewport (offset past the
            // leading sidebar when expanded — see DesktopShell `--content-inset-start`).
            paddingInlineStart: 'calc(1rem + var(--content-inset-start, 0px))',
          }}
        >
          <div className="pointer-events-none flex flex-col items-center gap-2">
            {/* Why-disabled hint: the free design step comes first (funnel
                rule). Chip backdrop keeps it readable over the content. */}
            {!hasCompletedMainRender && (
              <span className="rounded-full bg-muted/90 px-3 py-1 text-card-meta text-muted-foreground shadow-sm backdrop-blur">
                {tCommon('generateDesignFirst')}
              </span>
            )}
          <Button
            onClick={handleGenerateClick}
            // Disabled until the first design exists — see handleGenerateAlbum.
            disabled={isAlbumBusy || !hasCompletedMainRender}
            size="cta"
            className="pointer-events-auto shadow-lg shadow-black/20"
          >
            {isGenerating || hasAlbumInQueue ? (
              <>
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
                {t('generatingAlbum')}
              </>
            ) : (
              <>
                {albums.length > 0
                  ? t('generateNewVersion')
                  : t('generateAlbum')}
                {/* Server-predicted token cost — suffix only when the server
                    flags the cost as significant (threshold lives server-side;
                    loading/error/below-threshold → plain label, no price) and
                    only for legacy users (P4.3). */}
                {showsTokenCost && predictedTokens !== undefined && isSignificantCost && (
                  <span className="inline-flex items-center gap-1">
                    ~{predictedTokens}
                    <WalnutIcon className="h-3.5 w-[11px] shrink-0" />
                  </span>
                )}
              </>
            )}
          </Button>
          </div>
        </div>
      )}
    </div>
  );
}
